/**
 * @module engine3d/camera
 * @summary Orbit camera with switchable perspective / isometric projection.
 * @description
 *   The camera is a target point plus spherical coordinates around it, which is
 *   the model every CAD viewer uses: drag orbits, right-drag or two-finger drag
 *   pans, wheel dollies. `kind` switches between a perspective projection (how
 *   the plant actually looks) and an orthographic one (true isometric, where
 *   parallel runs stay parallel and equal lengths measure equal) without
 *   changing anything else about the framing.
 */
import {
  clamp,
  deg,
  mLookAt,
  perpendicular,
  v3,
  vAdd,
  vCross,
  vMul,
  vNorm,
  vSub,
  xfPoint,
  type Mat4,
  type Vec3,
} from './math3';

export type ProjectionKind = 'perspective' | 'ortho';

/**
 * Dolly limits. The near end used to be 40, which on a valve a few units
 * across meant you could never actually get up to it.
 */
const MIN_DIST = 6;
const MAX_DIST = 20000;

/**
 * Zoom factor for one wheel event.
 *
 * Proportional to how far the wheel actually moved, so a trackpad's handful of
 * pixels gives a fine nudge and a mouse notch gives a modest step, rather than
 * both jumping by the same fixed amount. The base is deliberately gentle: one
 * notch moves about six percent, so closing in on something is a smooth roll
 * instead of three lurches that overshoot.
 *
 * `deltaMode` matters: browsers report lines and pages as well as pixels, and
 * three lines is not three pixels. The result is clamped so a mouse reporting
 * enormous deltas cannot teleport the camera in one event.
 */
export function wheelZoomFactor(deltaY: number, deltaMode = 0): number {
  const perUnit = deltaMode === 1 ? 16 : deltaMode === 2 ? 100 : 1;
  const d = clamp(deltaY * perUnit, -300, 300);
  return Math.pow(1.0006, d);
}

/** A named camera position the toolbar can jump to. */
export type ViewPreset = 'iso' | 'isoSW' | 'isoNE' | 'top' | 'front' | 'side';

export class Camera {
  /** Point the camera orbits and looks at. */
  target: Vec3 = v3(0, 0, 0);
  /** Horizontal angle, radians. 0 looks along +Z. */
  azimuth = deg(-35);
  /** Vertical angle, radians. Clamped short of the poles to keep `up` stable. */
  elevation = deg(28);
  /** Distance from the target. */
  distance = 800;
  kind: ProjectionKind = 'perspective';
  /** Vertical field of view for the perspective projection. */
  fov = deg(38);
  near = 4;

  /** The eye position implied by the orbit parameters. */
  eye(): Vec3 {
    const ce = Math.cos(this.elevation);
    return vAdd(this.target, {
      x: Math.sin(this.azimuth) * ce * this.distance,
      y: Math.sin(this.elevation) * this.distance,
      z: Math.cos(this.azimuth) * ce * this.distance,
    });
  }

  view(): Mat4 {
    return mLookAt(this.eye(), this.target, v3(0, 1, 0));
  }

  /**
   * Projection scale in pixels per world unit at the target plane.
   * Both projections are applied by hand in the renderer (see `project`), so
   * this is all either of them needs.
   */
  scale(viewportH: number): number {
    return viewportH / (2 * Math.tan(this.fov / 2));
  }

  orbit(dAz: number, dEl: number): void {
    this.azimuth += dAz;
    // Stop just short of straight down: at the pole the up vector degenerates
    // and the view snaps through 180°, which feels like the model jumped.
    this.elevation = clamp(this.elevation + dEl, deg(-8), deg(88));
  }

  dolly(factor: number): void {
    this.distance = clamp(this.distance * factor, MIN_DIST, MAX_DIST);
  }

  /**
   * Zoom toward a world point, keeping that point under the cursor.
   *
   * This is the difference between usable and unusable navigation on a dense
   * layout. Zooming toward the TARGET — which is what a plain dolly does —
   * means the only thing you can close in on is whatever happens to be in the
   * middle of the screen, so reaching anything else costs a pan-zoom-pan-zoom
   * loop. Zooming toward the cursor lets you go straight there.
   *
   * The maths is short because the orientation never changes: scaling the
   * eye-to-point vector by the same factor as the distance leaves the point's
   * screen position algebraically unchanged, under both projections.
   */
  zoomAt(p: Vec3, factor: number): void {
    const eye = this.eye();
    const dir = vNorm(vSub(this.target, eye));
    const want = this.distance * factor;
    const next = clamp(want, MIN_DIST, MAX_DIST);
    // If the clamp bit, scale by what we actually got so the point still holds.
    const f = this.distance > 1e-6 ? next / this.distance : factor;
    const newEye = vAdd(p, vMul(vSub(eye, p), f));
    this.distance = next;
    this.target = vAdd(newEye, vMul(dir, next));
  }

  /** Pan the target across the camera's own screen plane. */
  pan(dxPx: number, dyPx: number, viewportH: number): void {
    const eye = this.eye();
    const fwd = vNorm({ x: this.target.x - eye.x, y: this.target.y - eye.y, z: this.target.z - eye.z });
    const right = vNorm(vCross(fwd, v3(0, 1, 0)));
    const up = vCross(right, fwd);
    // World units per pixel at the target plane — identical for both
    // projections, because ortho uses the same scale factor.
    const k = this.distance / this.scale(viewportH);
    this.target = vAdd(this.target, vAdd(vMul(right, -dxPx * k), vMul(up, dyPx * k)));
  }

  /**
   * Frame an axis-aligned box, leaving a small margin around it.
   *
   * The obvious implementation fits the box's bounding SPHERE, which is what
   * this used to do — and on a production line, which is long, low and thin,
   * the sphere is dominated by the diagonal and leaves the plant filling barely
   * half the frame. This instead measures the box's true extent along the
   * camera's own right / up axes, so the fit is exact for the direction you are
   * actually looking from.
   */
  fit(min: Vec3, max: Vec3, aspect: number, margin = 1.07): void {
    const cx = (min.x + max.x) / 2;
    const cy = (min.y + max.y) / 2;
    const cz = (min.z + max.z) / 2;
    // Bias the target slightly low so the plant sits in the lower two-thirds,
    // which leaves room for the labels that float above it.
    this.target = v3(cx, cy * 0.88, cz);

    // Camera basis. `toEye` points from the target back toward the camera.
    const ce = Math.cos(this.elevation);
    const toEye = vNorm(v3(Math.sin(this.azimuth) * ce, Math.sin(this.elevation), Math.cos(this.azimuth) * ce));
    const fwd = vMul(toEye, -1);
    let right = vCross(fwd, v3(0, 1, 0));
    // Looking straight down collapses the cross product; fall back to the same
    // perpendicular the view matrix picks so the fit matches what is rendered.
    right = Math.hypot(right.x, right.y, right.z) < 1e-6 ? perpendicular(fwd) : vNorm(right);
    const up = vCross(right, fwd);

    const vHalf = this.fov / 2;
    const hHalf = Math.atan(Math.tan(vHalf) * Math.max(0.2, aspect));
    const tanH = Math.tan(hHalf);
    const tanV = Math.tan(vHalf);
    const persp = this.kind === 'perspective';

    // Solve the distance PER CORNER rather than from the box's separate maximum
    // extents. A corner that is far off-axis is usually not the corner nearest
    // the camera, so combining the two maxima independently pushes the camera
    // much further back than any single corner actually requires.
    let need = 12;
    let nearest = 0;
    for (const px of [min.x, max.x])
      for (const py of [min.y, max.y])
        for (const pz of [min.z, max.z]) {
          const dx = px - this.target.x;
          const dy = py - this.target.y;
          const dz = pz - this.target.z;
          const a = Math.abs(dx * right.x + dy * right.y + dz * right.z);
          const b = Math.abs(dx * up.x + dy * up.y + dz * up.z);
          // How far this corner sits toward the camera along the view axis.
          const c = persp ? dx * toEye.x + dy * toEye.y + dz * toEye.z : 0;
          need = Math.max(need, a / tanH + c, b / tanV + c);
          nearest = Math.max(nearest, c);
        }
    // Keep the closest corner comfortably in front of the near plane.
    this.distance = clamp(Math.max(need * margin, nearest + this.near * 4 + 10), MIN_DIST, MAX_DIST);
  }

  /**
   * Frame the actual geometry rather than its bounding box.
   *
   * A production line is long, thin and usually laid out on a diagonal, so its
   * axis-aligned box is mostly empty air — fitting the box exactly still leaves
   * the plant filling about half the frame. Solving over the real vertices
   * costs a fraction of a millisecond (it runs on Fit, not per frame) and
   * frames the drawing the way a person would.
   *
   * @param pos     Packed xyz vertex positions (the scene's triangle buffer).
   * @param count   Number of vertices to consider.
   * @param hs      Height scale currently applied by the renderer.
   */
  fitPoints(
    pos: Float32Array,
    count: number,
    centre: Vec3,
    aspect: number,
    hs = 1,
    margin = 1.06,
  ): void {
    if (count < 3) return;
    this.target = v3(centre.x, centre.y * 0.88, centre.z);
    const ce = Math.cos(this.elevation);
    const toEye = vNorm(v3(Math.sin(this.azimuth) * ce, Math.sin(this.elevation), Math.cos(this.azimuth) * ce));
    const fwd = vMul(toEye, -1);
    let right = vCross(fwd, v3(0, 1, 0));
    right = Math.hypot(right.x, right.y, right.z) < 1e-6 ? perpendicular(fwd) : vNorm(right);
    const up = vCross(right, fwd);

    const vHalf = this.fov / 2;
    const hHalf = Math.atan(Math.tan(vHalf) * Math.max(0.2, aspect));
    const tanH = Math.tan(hHalf);
    const tanV = Math.tan(vHalf);
    const persp = this.kind === 'perspective';

    /** Smallest distance at which every vertex is inside the frustum. */
    const solve = (): number => {
      let need = 12;
      let nearest = 0;
      for (let i = 0; i < count; i++) {
        const dx = pos[i * 3] - this.target.x;
        const dy = pos[i * 3 + 1] * hs - this.target.y;
        const dz = pos[i * 3 + 2] - this.target.z;
        const a = Math.abs(dx * right.x + dy * right.y + dz * right.z);
        const b = Math.abs(dx * up.x + dy * up.y + dz * up.z);
        const c = persp ? dx * toEye.x + dy * toEye.y + dz * toEye.z : 0;
        const req = Math.max(a / tanH, b / tanV) + c;
        if (req > need) need = req;
        if (c > nearest) nearest = c;
      }
      return clamp(Math.max(need * margin, nearest + this.near * 4 + 10), MIN_DIST, MAX_DIST);
    };

    this.distance = solve();

    // Re-centre. Under perspective the near end of a long line magnifies, so
    // the silhouette is NOT centred on the box centre and the solve above ends
    // up satisfying one edge with slack at the other. Sliding the target onto
    // the true silhouette centre and re-solving recovers that slack — it is the
    // difference between the plant filling two-thirds of the frame and filling
    // it. Two passes is comfortably enough to converge.
    for (let pass = 0; pass < 2; pass++) {
      let aMin = Infinity;
      let aMax = -Infinity;
      let bMin = Infinity;
      let bMax = -Infinity;
      for (let i = 0; i < count; i++) {
        const dx = pos[i * 3] - this.target.x;
        const dy = pos[i * 3 + 1] * hs - this.target.y;
        const dz = pos[i * 3 + 2] - this.target.z;
        const c = persp ? dx * toEye.x + dy * toEye.y + dz * toEye.z : 0;
        // Offsets in tangent space, i.e. what the projection actually shows.
        const k = 1 / Math.max(this.near, this.distance - c);
        const a = (dx * right.x + dy * right.y + dz * right.z) * k;
        const b = (dx * up.x + dy * up.y + dz * up.z) * k;
        if (a < aMin) aMin = a;
        if (a > aMax) aMax = a;
        if (b < bMin) bMin = b;
        if (b > bMax) bMax = b;
      }
      if (!Number.isFinite(aMin)) break;
      const shiftA = ((aMin + aMax) / 2) * this.distance;
      const shiftB = ((bMin + bMax) / 2) * this.distance;
      this.target = vAdd(this.target, vAdd(vMul(right, shiftA), vMul(up, shiftB)));
      this.distance = solve();
    }
  }

  /**
   * The world-space ray under a screen pixel.
   *
   * This is what turns a mouse drag into a position in the plant: every gizmo
   * handle resolves its new value by intersecting this ray with the axis or
   * plane it is constrained to. Both projections are handled, because in
   * orthographic the rays are parallel and it is the ORIGIN that moves across
   * the screen, not the direction.
   */
  rayFrom(px: number, py: number, viewportW: number, viewportH: number): { o: Vec3; d: Vec3 } {
    const eye = this.eye();
    const fwd = vNorm(vSub(this.target, eye));
    let right = vCross(fwd, v3(0, 1, 0));
    right = Math.hypot(right.x, right.y, right.z) < 1e-6 ? perpendicular(fwd) : vNorm(right);
    const up = vCross(right, fwd);
    const f = this.scale(viewportH);
    const sx = (px - viewportW / 2) / f;
    const sy = -(py - viewportH / 2) / f;
    if (this.kind === 'perspective') {
      return {
        o: eye,
        d: vNorm(vAdd(vAdd(vMul(right, sx), vMul(up, sy)), fwd)),
      };
    }
    const k = this.distance;
    return {
      o: vAdd(eye, vAdd(vMul(right, sx * k), vMul(up, sy * k))),
      d: fwd,
    };
  }

  /**
   * World units per screen pixel at the camera's target.
   * Gizmos multiply by this to stay a constant size on screen however far you
   * have zoomed out — a handle that shrinks with the model is unusable.
   */
  pixelScale(viewportH: number): number {
    return this.distance / this.scale(viewportH);
  }

  /**
   * Project a world point to screen pixels, or null if it is behind the camera.
   * The pipe tool uses this to decide which world axis the cursor is actually
   * being dragged along — a decision that can only be made in screen space.
   */
  projectPoint(p: Vec3, viewportW: number, viewportH: number): { x: number; y: number; z: number } | null {
    const q = xfPoint(this.view(), p);
    const clip = this.kind === 'perspective' ? -this.near : Infinity;
    if (q.z > clip) return null;
    const f = this.scale(viewportH);
    const k = this.kind === 'perspective' ? f / Math.max(this.near, -q.z) : f / this.distance;
    return { x: viewportW / 2 + q.x * k, y: viewportH / 2 - q.y * k, z: q.z };
  }

  /** Unit vector from the camera toward its target. */
  forward(): Vec3 {
    return vNorm(vSub(this.target, this.eye()));
  }

  /** Jump to a standard viewpoint, keeping the current target and distance. */
  preset(p: ViewPreset): void {
    switch (p) {
      case 'iso':
        this.azimuth = deg(-35);
        this.elevation = deg(28);
        this.kind = 'ortho';
        break;
      case 'isoSW':
        this.azimuth = deg(35);
        this.elevation = deg(30);
        break;
      case 'isoNE':
        this.azimuth = deg(-135);
        this.elevation = deg(30);
        break;
      case 'top':
        this.azimuth = deg(0);
        this.elevation = deg(87);
        break;
      case 'front':
        this.azimuth = deg(0);
        this.elevation = deg(4);
        break;
      case 'side':
        this.azimuth = deg(-90);
        this.elevation = deg(4);
        break;
    }
  }
}
